import '../../OrganizationTables.scss';
import './ClassList.scss';
import '../../../Common.scss';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Class } from 'types/organization/classes';
import { Student } from 'types/organization/students';
import { filterOrganizationInvitesByClass, filterOrganizationStudentsByClass } from 'utilities/organizationDataFilter';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import { ReactComponent as AddStudentsIcon } from 'images/icons/AddUsers.svg';
import { Button, Checkbox, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import RenameClassModal from '../RenameClassModal/RenameClassModal';
import DeleteClassesModal from '../DeleteClassesModal/DeleteClassesModal';
import { AddStudentsToClass } from '../AddStudentsToClass/AddStudentsToClass';
import { AddNewClassButton } from '../AddNewClassButton/AddNewClassButton';
import { Invite } from 'types/organization/invites';

type Props = {
  classList: Class[];
  studentList: Student[];
  inviteList: Invite[];
};

type ClassListEntry = {
  id: string;
  name: string;
  numberOfStudents: number;
};

export const ClassList = ({ classList, studentList, inviteList }: Props) => {
  const { t } = useTranslation();

  const [selectedClassIds, setSelectedClasstIds] = useState<string[]>([]);
  const [isAddStudentsModalOn, setIsAddStudentsModalOn] = useState(false);
  const [isEditClassModalOn, setEditClassModalOn] = useState(false);
  const [isDeleteClassesModalOn, setIsDeleteClassesModalOn] = useState(false);
  const [classIdUnderAction, setClassIdUnderAction] = useState<string>('');

  const tableEntries: ClassListEntry[] = classList.map((c) => ({
    id: c.id,
    name: c.name,
    numberOfStudents:
      filterOrganizationStudentsByClass(studentList, c.id).length + filterOrganizationInvitesByClass(inviteList, c.id).length,
  }));

  const handleSelectAll = () => {
    if (selectedClassIds.length === classList.length) {
      setSelectedClasstIds([]);
    } else {
      setSelectedClasstIds(classList.map((c) => c.id));
    }
  };

  const handleSelectEntry = (id: string) => {
    if (selectedClassIds.includes(id)) {
      setSelectedClasstIds(selectedClassIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedClasstIds([...selectedClassIds, id]);
    }
  };

  const handleOpenAddStudentsDialog = (id: string) => {
    setClassIdUnderAction(id);
    setIsAddStudentsModalOn(true);
  };

  const handleOpenEditClassDialog = (id: string) => {
    setClassIdUnderAction(id);
    setEditClassModalOn(true);
  };

  const handleOpenRemoveEntryDialog = (id: string) => {
    setClassIdUnderAction(id);
    setIsDeleteClassesModalOn(true);
  };

  const handleRenameClass = () => {
    setEditClassModalOn(false);
    setClassIdUnderAction('');
  };

  const handleAddStudentsToClass = () => {
    setIsAddStudentsModalOn(false);
    setClassIdUnderAction('');
  };

  const handleDeleteClasses = () => {
    setIsDeleteClassesModalOn(false);
    if (classIdUnderAction) {
      setClassIdUnderAction('');
    } else {
      setSelectedClasstIds([]);
    }
  };

  const getEditedClass = () => classList.find((c) => c.id === classIdUnderAction);

  return (
    <div className='organization-tab-content classes-tab-content'>
      <AddNewClassButton intent='primary' />
      <div className='organization-table-card-wrapper'>
        <div className='organization-table-header'>
          <strong>
            {t('Classes', { ns: 'studentsPage' })} ({classList.length})
          </strong>
          <Button
            startIcon={<TrashIcon />}
            label={t('DeleteClasses', { ns: 'classPage' })}
            className='organization-button'
            onClick={() => setIsDeleteClassesModalOn(true)}
            intent='secondary'
            disabled={selectedClassIds.length === 0}
          />
        </div>
        <Table sx={{ minWidth: 750 }} aria-labelledby='classesTable' className='tmu-table'>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox'>
                <Checkbox
                  color='primary'
                  indeterminate={selectedClassIds.length > 0 && selectedClassIds.length < tableEntries.length}
                  checked={tableEntries.length > 0 && selectedClassIds.length === tableEntries.length}
                  onChange={() => handleSelectAll()}
                  label={undefined}
                />
              </TableCell>
              <TableCell sx={{ width: 500 }}>{t('ClassName', { ns: 'organizationPage' })}</TableCell>
              <TableCell sx={{ width: 500 }}>{t('NumberOfStudents', { ns: 'classPage' })}</TableCell>
              <TableCell sx={{ width: 150 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableEntries.map((entry, index) => (
              <TableEntry
                key={entry.id}
                isItemSelected={selectedClassIds.includes(entry.id)}
                tableEntry={entry}
                handleSelectEntry={() => handleSelectEntry(entry.id)}
                openAddStudentsDialog={() => handleOpenAddStudentsDialog(entry.id)}
                openEditClassDialog={() => handleOpenEditClassDialog(entry.id)}
                openRemoveEntryDialog={() => handleOpenRemoveEntryDialog(entry.id)}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      {isEditClassModalOn && (
        <RenameClassModal isOpen={isEditClassModalOn} editedClass={getEditedClass() as Class} close={handleRenameClass} />
      )}
      {isAddStudentsModalOn && (
        <AddStudentsToClass isOpen={isAddStudentsModalOn} close={handleAddStudentsToClass} selectedClassId={classIdUnderAction} />
      )}
      {isDeleteClassesModalOn && (
        <DeleteClassesModal
          isOpen={isDeleteClassesModalOn}
          close={handleDeleteClasses}
          selectedIds={classIdUnderAction ? [classIdUnderAction] : selectedClassIds}
        />
      )}
    </div>
  );
};

const TableEntry = ({
  isItemSelected,
  tableEntry,
  handleSelectEntry,
  openEditClassDialog,
  openAddStudentsDialog,
  openRemoveEntryDialog,
}) => {
  const selectEntry = (event) => {
    event.stopPropagation();
    handleSelectEntry();
  };

  const editClass = (event) => {
    event.stopPropagation();
    openEditClassDialog();
  };

  const addStudent = (event) => {
    event.stopPropagation();
    openAddStudentsDialog();
  };

  const removeEntry = (event) => {
    event.stopPropagation();
    openRemoveEntryDialog();
  };

  return (
    <TableRow
      hover
      onClick={(event) => selectEntry(event)}
      role='checkbox'
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={tableEntry.id}
      selected={isItemSelected}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell padding='checkbox'>
        <Checkbox color='primary' checked={isItemSelected} label={undefined} />
      </TableCell>
      <TableCell>{tableEntry.name}</TableCell>
      <TableCell>{tableEntry.numberOfStudents}</TableCell>
      <TableCell>
        <div className='buttons-cell-content'>
          <IconButton icon={() => <PencilIcon />} onClick={editClass} />
          <IconButton icon={() => <AddStudentsIcon />} onClick={addStudent} />
          <IconButton icon={() => <TrashIcon />} onClick={removeEntry} />
        </div>
      </TableCell>
    </TableRow>
  );
};
